<script setup lang="ts">
/**
 * A dialog.
 *
 * The classes are `dlg` rather than `modal` on purpose. Bootstrap is loaded
 * across the application and owns `.modal`, where it means a hand-rolled
 * overlay that starts life `display: none` and is shown by its own JavaScript.
 * Taking that name gave every dialog here an invisible one.
 *
 * Built on the native `<dialog>`, which brings the things a hand-rolled modal
 * usually gets wrong: focus moves into it and is trapped there, the page
 * behind becomes inert, Escape closes it, and it sits above everything without
 * a z-index arms race.
 *
 * Closing is always possible. A dialog somebody cannot dismiss is a dialog
 * that will trap them when something behind it has failed.
 */
import { useI18n } from 'vue-i18n'

import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    /** Roughly how much room the content needs. */
    size?: 'small' | 'medium' | 'large' | 'xlarge' | 'full'
  }>(),
  { size: 'medium' },
)

const emit = defineEmits<{
  close: []
}>()

const dialog = ref<HTMLDialogElement | null>(null)

function sync(open: boolean): void {
  const element = dialog.value
  if (!element) return

  if (open && !element.open) element.showModal()
  if (!open && element.open) element.close()
}

watch(() => props.open, sync)
onMounted(() => sync(props.open))

// The browser closes it on Escape without telling the caller, which would
// leave the caller's own flag saying it is still open.
function onClose(): void {
  emit('close')
}

onBeforeUnmount(() => {
  if (dialog.value?.open) dialog.value.close()
})
</script>

<template>
  <dialog ref="dialog" class="dlg" :class="`dlg--${size}`" @close="onClose" @cancel="onClose">
    <div class="dlg__frame builder-root">
      <header class="dlg__head">
        <h2 class="dlg__title">{{ title }}</h2>
        <button
          type="button"
          class="b-icon-btn"
          :aria-label="t('common.close')"
          @click="emit('close')"
        >
          <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M4 4l8 8M12 4l-8 8"
              fill="none"
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </header>

      <div class="dlg__body">
        <slot />
      </div>

      <footer v-if="$slots['actions']" class="dlg__foot">
        <slot name="actions" />
      </footer>
    </div>
  </dialog>
</template>

<style scoped>
.dlg {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--ink);
  max-width: none;
  max-height: none;
}

.dlg::backdrop {
  background: var(--color-overlay);
  backdrop-filter: blur(2px);
}

.dlg__frame {
  display: flex;
  flex-direction: column;
  width: min(92vw, var(--dlg-width, 560px));
  max-height: 86vh;
  border: 1px solid var(--hairline);
  border-radius: var(--radius-card);
  background: var(--panel);
  box-shadow: var(--lift-3);
  overflow: hidden;
}

.dlg--small .dlg__frame {
  --dlg-width: 420px;
}

.dlg--large .dlg__frame {
  --dlg-width: 860px;
}

/* For the two pickers, which put a list beside a preview. Anything narrower
   makes one of the two useless, and there is no point showing a preview
   nobody can read. */
.dlg--xlarge .dlg__frame {
  --dlg-width: 1240px;
  max-height: 90vh;
}

/* For a workspace rather than a question: nearly the whole window, at a fixed
   height, and a body that does not scroll or pad itself, because what is in
   it lays out its own panes and scrolls each of them on its own. */
.dlg--full .dlg__frame {
  --dlg-width: 1680px;
  width: min(96vw, var(--dlg-width));
  height: 92vh;
  max-height: 92vh;
}

.dlg--full .dlg__body {
  padding: 0;
  overflow: hidden;
}

/* The kit's Dialog: a title of some weight, no rule under it, the body
   straight beneath. A workspace-sized one keeps a rule, since what is under
   it is panes rather than a paragraph. */
.dlg__head {
  display: flex;
  align-items: center;
  gap: var(--sp-5);
  padding: 20px var(--sp-7) var(--sp-2);
  flex: none;
}

.dlg--xlarge .dlg__head,
.dlg--full .dlg__head {
  padding-bottom: var(--sp-5);
  border-bottom: 1px solid var(--hairline);
}

.dlg__title {
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--weight-semibold);
  letter-spacing: -0.01em;
  color: var(--ink);
}

.dlg__head .b-icon-btn {
  margin-left: auto;
}

.dlg__body {
  padding: var(--sp-5) var(--sp-7) var(--sp-7);
  overflow: auto;
  flex: 1;
  min-height: 0;
}

.dlg__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--sp-4);
  padding: var(--sp-6) var(--sp-7);
  border-top: 1px solid var(--hairline);
  background: var(--panel);
  flex: none;
}
</style>
